
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import Editor from '@tinymce/tinymce-vue';
import ServicoArquivo from '@/servicos/ServicoArquivo';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';

const __default__ = defineComponent({
  name: 'EditorHtml',
  props: {
    value: {
      type: String,
      default: '',
    },
    minHeight: {
      type: String,
      default: '100',
    },
    maxHeight: {
      type: String,
      default: '500',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Editor,
  },
  emits: ['update:value', 'change', 'blur'],
  setup(props, { emit }) {
    const servicoArquivo = new ServicoArquivo();
    const alturaMinima = ref(`${props.minHeight}px`);
    const alturaMaxima = ref(`${props.maxHeight}px`);
    const state = reactive({
      computedValue: computed({
        get: () => props.value,
        set: (val: string) => {
          emit('update:value', val);

          if (val !== props.value) {
            emit('change', val);
          }
        },
      }),
    });

    async function exampleImageUploadHandler(blobInfo:any) {
      const arquivo = await servicoArquivo.enviar(new File([blobInfo.blob()], blobInfo.filename()));
      return arquivo.link;
    }

    function blur() {
      emit('blur');
    }

    function sanitizeHtmlForReport(html: string): string {
      if (!html) return '';

      const allowedTags = new Set([
        'P', 'SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'BR', 'FONT', 'OL', 'UL', 'LI',
      ]);

      const stylePropsToKeep = new Set([
        'font-size',
        'font-weight',
        'font-style',
        'text-decoration',
        'text-decoration-line', // algumas ferramentas usam isso
        'background-color',
      ]);

      const container = document.createElement('div');
      container.innerHTML = html;

      const cleanNode = (node: Node) => {
        // Comentários (inclui os do Word)
        if (node.nodeType === Node.COMMENT_NODE) {
      // eslint-disable-next-line no-unused-expressions
      node.parentNode?.removeChild(node);
      return;
        }

        // Texto: normaliza &nbsp;
        if (node.nodeType === Node.TEXT_NODE) {
          // eslint-disable-next-line no-param-reassign
          node.nodeValue = node.nodeValue?.replace(/\u00A0/g, ' ') ?? '';
          return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const el = node as HTMLElement;
        const tag = el.tagName;

        // 1) Converter bullets do Word (Wingdings ü) para bolinha "• "
        const rawStyle = (el.getAttribute('style') || '').toLowerCase();
        if (
          tag === 'SPAN'
      && /font-family\s*:\s*wingdings/.test(rawStyle)
      && el.textContent?.includes('ü')
        ) {
          el.textContent = el.textContent.replace(/ü+/g, '• ');
          el.removeAttribute('style'); // estilo Wingdings não é mais necessário
        }

        // 2) Processa filhos primeiro
        Array.from(el.childNodes).forEach(cleanNode);

        // 3) Se a tag não é permitida, desembrulha
        if (!allowedTags.has(tag)) {
          const parent = el.parentNode;
          if (parent) {
            while (el.firstChild) {
              parent.insertBefore(el.firstChild, el);
            }
            parent.removeChild(el);
          }
          return;
        }

        // 4) Limpa atributos, exceto style e alguns de <font>
        Array.from(el.attributes).forEach((attr) => {
          const name = attr.name.toLowerCase();

          if (name === 'style') return; // trataremos abaixo

          // <font color="..." size="...">
          if (tag === 'FONT' && (name === 'color' || name === 'size')) return;

          // Remove data-mce-*, class, id, etc.
          el.removeAttribute(attr.name);
        });

        // 5) Filtra o style, preservando cor / tamanho / sublinhado etc.
        const style = el.getAttribute('style');
        if (style) {
          const kept: string[] = [];

          style
            .split(';')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((rule) => {
              const [rawName, ...rest] = rule.split(':');
              if (!rest.length) return;

              const name = rawName.trim().toLowerCase();
              const value = rest.join(':').trim();

              // ignora lixo do Word
              if (name.startsWith('mso-')) return;

              if (stylePropsToKeep.has(name)) {
                kept.push(`${name}: ${value}`);
              }
            });

          if (kept.length) {
            el.setAttribute('style', kept.join('; '));
          } else {
            el.removeAttribute('style');
          }
        }
      };

      Array.from(container.childNodes).forEach(cleanNode);

      Array.from(container.querySelectorAll('*')).forEach((el: any) => {
        const text = el.textContent?.replace(/\s+/g, '') ?? '';
        if (!text && !['BR', 'LI'].includes(el.tagName)) {
          el.remove();
        }
      });
      return container.innerHTML.trim();
    }

    function validarInformacaoColada(evento:any) {
      evento.preventDefault();
      const valorColado = evento.clipboardData.getData('text');
      if (UtilitarioGeral.valorValido(valorColado)) {
        state.computedValue = sanitizeHtmlForReport(valorColado);
      }
    }

    return {
      props,
      alturaMinima,
      alturaMaxima,
      state,
      blur,
      exampleImageUploadHandler,
      validarInformacaoColada,
      sanitizeHtmlForReport,
    };
  },
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "3f7018bc": (_ctx.alturaMinima),
  "40487960": (_ctx.alturaMaxima)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__